import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

/**
 * Opens the existing cart drawer via its public API, if one is present on the page.
 * Reuses the already-existing cart-drawer-component instance and its existing
 * open() method; does not create a new drawer, fetch the cart again, or touch
 * scroll-lock/focus directly (the drawer's own dialog logic owns that).
 */
function openExistingCartDrawer() {
  const cartDrawer = document.querySelector('cart-drawer-component');
  if (cartDrawer && typeof cartDrawer.open === 'function') {
    cartDrawer.open();
  }
}

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();
  /** @type {MutationObserver | null} */
  #galleryObserver = null;
  /** Monotonic token used to ignore stale desktop thumbnail preparations. */
  #quickAddMediaGeneration = 0;

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));

    // Scoped to `this` (not `document`): only catches cart-add events that bubble
    // from this component's own inline product form, i.e. the single-variant
    // direct-add path. The multi-variant modal's form lives inside the separate
    // #quick-add-dialog element and never bubbles through here, and the standard
    // product page has no quick-add-component ancestor at all, so neither of
    // those flows is affected by this listener.
    this.addEventListener(ThemeEvents.cartUpdate, this.#handleOwnCartAdd, {
      signal: this.#cartUpdateAbortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
    this.#galleryObserver?.disconnect();
    this.#galleryObserver = null;
    this.#quickAddMediaGeneration += 1;
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Handles a successful cart-add that originated from this component's own
   * single-variant direct add-to-cart button. Opens the existing cart drawer.
   * Does nothing on error, and does nothing for cart-add events from elsewhere
   * on the page (see the scoping note where this listener is registered).
   * @param {CustomEvent<{data: {didError?: boolean}}>} event
   */
  #handleOwnCartAdd = (event) => {
    if (event.detail?.data?.didError) return;
    openExistingCartDrawer();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          const shortDescription = this.#getQuickAddShortDescription(html);
          if (shortDescription) productGrid.setAttribute('data-selu-quick-add-description', shortDescription);
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
    }

    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Builds a short text for desktop quick-buy from product-page metadata.
   * This keeps Quick Buy compact and avoids product-specific hardcoding.
   * @param {Document} html
   * @returns {string}
   */
  #getQuickAddShortDescription(html) {
    const metaDescription = html.querySelector('meta[name="description"]')?.getAttribute('content') || '';
    const text = metaDescription.replace(/\s+/g, ' ').trim();
    if (!text) return '';
    return text.length > 150 ? `${text.slice(0, 147).trim()}…` : text;
  }

  /**
   * Enhances desktop quick-buy only. Mobile quick-buy is intentionally untouched.
   * @param {Element} modalContent
   * @param {string} shortDescription
   */
  #setupDesktopQuickAdd(modalContent, shortDescription) {
    if (isMobileBreakpoint()) return;

    modalContent.setAttribute('data-selu-desktop-quickbuy', 'true');
    this.#setupDesktopQuickAddMedia(modalContent);
    this.#setupDesktopQuickAddDescription(modalContent, shortDescription);
    this.#setupDesktopQuickAddDetailsLink(modalContent);
  }

  /**
   * Creates a safe desktop-only main image + up to 3 thumbnail selector.
   * It does not change product media Liquid, image URLs, or mobile behavior.
   *
   * Called once on initial quickbuy open and automatically re-called whenever
   * media-gallery is replaced by variant-picker's VariantUpdateEvent handler
   * (media-gallery.js calls this.replaceWith(newMediaGallery) after each
   * section-render fetch triggered by #syncVariantSelection or a swatch click).
   *
   * @param {Element} modalContent
   */
  #setupDesktopQuickAddMedia(modalContent) {
    this.#galleryObserver?.disconnect();
    this.#galleryObserver = null;

    const mediaArea = modalContent.querySelector('.product-information__media');
    if (!mediaArea) return;

    // Observe only gallery replacements. Thumbnail commits performed below do
    // not retrigger this path.
    this.#galleryObserver = new MutationObserver((mutations) => {
      const galleryReplaced = mutations.some((mutation) =>
        [...mutation.addedNodes].some(
          (node) => node instanceof Element && node.tagName.toLowerCase() === 'media-gallery'
        )
      );
      if (!galleryReplaced) return;
      void this.#refreshDesktopQuickAddMedia(mediaArea);
    });
    this.#galleryObserver.observe(mediaArea, { childList: true });

    void this.#refreshDesktopQuickAddMedia(mediaArea);
  }

  /**
   * Prepares a complete thumbnail group outside the visible DOM and replaces
   * the previous group only after every included image is decoded. The old
   * thumbnails remain visible during preparation.
   *
   * @param {Element} mediaArea
   */
  async #refreshDesktopQuickAddMedia(mediaArea) {
    const generation = ++this.#quickAddMediaGeneration;
    const mediaGallery = mediaArea.querySelector('media-gallery');
    const slides = Array.from(mediaGallery?.querySelectorAll('slideshow-slide') || []).filter((slide) =>
      slide.querySelector('img.product-media__image')
    );

    if (!mediaGallery || slides.length === 0) return;

    const thumbnailSourceSlides = slides.slice(0, 3);
    const thumbnails = document.createElement('div');
    thumbnails.className = 'selu-quickadd-thumbnails';
    thumbnails.setAttribute('aria-label', 'Produktbilder');

    const setActiveSlide = (index) => {
      const currentGallery = mediaArea.querySelector('media-gallery');
      if (!currentGallery) return;

      const currentSlides = Array.from(currentGallery.querySelectorAll('slideshow-slide')).filter((slide) =>
        slide.querySelector('img.product-media__image')
      );

      currentSlides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === index;
        slide.toggleAttribute('data-selu-active', isActive);
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });

      thumbnails.querySelectorAll('button').forEach((button) => {
        button.setAttribute('aria-selected', button.dataset.slideIndex === String(index) ? 'true' : 'false');
      });
    };

    // Keep the primary image visible immediately. Variant updates already
    // provide a decoded first slide; initial modal setup retains the previous
    // behavior instead of waiting for thumbnail preparation.
    setActiveSlide(0);

    const preparedButtons = await Promise.all(
      thumbnailSourceSlides.map(async (slide, index) => {
        const image = slide.querySelector('img.product-media__image');
        if (!(image instanceof HTMLImageElement)) return null;

        const thumbnail = /** @type {HTMLImageElement} */ (image.cloneNode(true));
        thumbnail.removeAttribute('id');
        thumbnail.removeAttribute('ref');
        thumbnail.loading = 'eager';
        thumbnail.decoding = 'async';
        thumbnail.fetchPriority = index === 0 ? 'high' : 'auto';
        thumbnail.className = 'selu-quickadd-thumbnail__image';

        const ready = await this.#waitForQuickAddImage(thumbnail);
        if (!ready) return null;

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'selu-quickadd-thumbnail';
        button.dataset.slideIndex = String(index);
        button.setAttribute('aria-label', `Produktbild ${index + 1} anzeigen`);
        button.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
        button.appendChild(thumbnail);
        button.addEventListener('click', (event) => {
          event.preventDefault();
          setActiveSlide(index);
        });

        return button;
      })
    );

    if (generation !== this.#quickAddMediaGeneration || mediaArea.querySelector('media-gallery') !== mediaGallery) return;

    preparedButtons.forEach((button) => {
      if (button) thumbnails.appendChild(button);
    });

    setActiveSlide(0);

    const previousThumbnails = mediaArea.querySelector('.selu-quickadd-thumbnails');

    if (thumbnails.childElementCount === 0) {
      previousThumbnails?.remove();
      return;
    }

    if (previousThumbnails) previousThumbnails.replaceWith(thumbnails);
    else mediaGallery.insertAdjacentElement('afterend', thumbnails);
  }

  /**
   * Waits until a detached Quick Buy image is downloaded and decoded before it
   * may become visible. Alt text remains intact for accessibility.
   *
   * @param {HTMLImageElement} image
   * @returns {Promise<boolean>}
   */
  #waitForQuickAddImage(image) {
    return new Promise((resolve) => {
      let settled = false;

      const finish = (ready) => {
        if (settled) return;
        settled = true;
        clearTimeout(timeoutId);
        resolve(ready);
      };

      const decode = async () => {
        if (image.naturalWidth <= 0) {
          finish(false);
          return;
        }

        try {
          await image.decode();
        } catch {
          // A decoded resource can still be usable after decode() rejects, for
          // example when the browser reports an interrupted redundant decode.
        }

        finish(image.naturalWidth > 0);
      };

      const timeoutId = window.setTimeout(() => finish(false), 10000);
      image.addEventListener('load', decode, { once: true });
      image.addEventListener('error', () => finish(false), { once: true });

      // The clone can become complete between creation and listener setup.
      if (image.complete) void decode();
    });
  }

  /**
   * Adds a short desktop-only product description without touching product data.
   * @param {Element} modalContent
   * @param {string} shortDescription
   */
  #setupDesktopQuickAddDescription(modalContent, shortDescription) {
    const text = shortDescription.trim();
    if (!text) return;

    const groupContent = modalContent.querySelector('.product-details > .group-block > .group-block-content');
    if (!groupContent || groupContent.querySelector('.selu-quickadd-description-wrapper')) return;

    const buyButtons = groupContent.querySelector('.buy-buttons-block');
    const wrapper = document.createElement('div');
    wrapper.className = 'view-more-details__wrapper selu-quickadd-description-wrapper';

    const paragraph = document.createElement('p');
    paragraph.className = 'selu-quickadd-short-description';
    paragraph.textContent = text;

    wrapper.appendChild(paragraph);
    if (buyButtons) buyButtons.before(wrapper);
    else groupContent.appendChild(wrapper);
  }

  /**
   * Adds a desktop-only “Alle Details anzeigen” link below Add-to-cart.
   * @param {Element} modalContent
   */
  #setupDesktopQuickAddDetailsLink(modalContent) {
    const buyButtons = modalContent.querySelector('.product-details .buy-buttons-block');
    if (!buyButtons || buyButtons.querySelector('.selu-quickadd-details-wrapper')) return;

    const wrapper = document.createElement('div');
    wrapper.className = 'view-more-details__wrapper selu-quickadd-details-wrapper';

    const link = document.createElement('a');
    link.className = 'view-more-details selu-quickadd-details-link';
    link.href = this.productPageUrl;
    link.textContent = 'Alle Details anzeigen';

    wrapper.appendChild(link);
    buyButtons.appendChild(wrapper);
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);

    // Mark desktop Quick Buy before variant synchronization so an immediate
    // gallery update uses the atomic image-readiness path in media-gallery.js.
    if (isMobileBreakpoint()) modalContent.removeAttribute('data-selu-desktop-quickbuy');
    else modalContent.setAttribute('data-selu-desktop-quickbuy', 'true');

    this.#syncVariantSelection(modalContent);
    this.#setupDesktopQuickAdd(
      modalContent,
      productGrid.getAttribute('data-selu-quick-add-description') || ''
    );
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productVariantsCount = this.dataset.productVariantsCount;
    const quickAddButton = productVariantsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog on a successful cart-add, then hands off to the existing
   * cart drawer. Waits for the dialog's own close lifecycle (closeDialog's
   * returned promise) to finish first, so focus/scroll-lock ownership transitions
   * from this dialog to the cart drawer cleanly instead of both dialogs
   * fighting over it. No fixed delay is used anywhere in this path.
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = async (event) => {
    if (event.detail.data.didError) return;
    await this.closeDialog();
    openExistingCartDrawer();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
